import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { requirePermission } from '@/lib/permissions/permissions'
import { Button } from '@/components/ui/button'
import { RxPlus } from 'react-icons/rx'
import { useState } from 'react'
import { getFormColumns, Form } from '@/components/forms/formColumns'
import NewFormDialog from '@/components/forms/NewFormDialog'
import { VirtualizedDataTable } from '@/components/dataTable/VirtualizedDataTable'
import DeleteFormAlert from '@/components/forms/DeleteFormAlert'
import { useOrgEntitlements } from '@/lib/hooks/useOrgEntitlements'
import {
  canUseLeadForms,
  canUsePublicIdentifier,
  canUsePublicLinks,
} from '@/lib/billing/plans'
import { Card, CardContent } from '@/components/ui/card'
import { useOrg } from '@/lib/states/session/hooks'
import { trpc } from '@/lib/trpc/client'

export const Route = createFileRoute('/_auth/_dashboard/forms/')({
  beforeLoad: () => {
    requirePermission('forms.edit')
  },
  component: FormsList,
})

function FormsList() {
  const [open, setOpen] = useState(false)
  const org = useOrg()
  const { data: forms, isPending: isFormsPending } = trpc.forms.list.useQuery(
    undefined,
    {
      enabled: Boolean(org),
    },
  )
  const navigate = useNavigate()
  const { data: entitlements } = useOrgEntitlements()
  const allowPublicLinks = canUsePublicLinks(entitlements)
  const allowPublicIdentifier = canUsePublicIdentifier(entitlements)
  const allowLeadForms = canUseLeadForms(entitlements)

  const [formToDelete, setFormToDelete] = useState<Form>()

  const handleOnClickEdit = (form: Form) => {
    if (open) {
      return
    }

    navigate({ to: '/forms/$formId', params: { formId: form.id } })
  }

  const handleOpenChange = (open: boolean) => {
    setOpen(open)
  }

  return (
    <>
      <DeleteFormAlert
        form={formToDelete}
        onOpenChange={() => {
          setFormToDelete(undefined)
        }}
      />
      <NewFormDialog open={open} onOpenChange={handleOpenChange} />
      <div className="flex h-full min-h-0 w-full flex-col">
        <div className="flex h-full min-h-0 w-full flex-col md:flex">
          <div className="flex min-h-0 flex-1 flex-col gap-4 p-8 pt-6">
            <div className="flex items-center justify-between gap-2">
              <h2 className="text-3xl font-bold tracking-tight">Forms</h2>
              <Button className="gap-2" onClick={() => setOpen(true)}>
                <RxPlus /> Add New
              </Button>
            </div>
            {!allowLeadForms && (
              <Card>
                <CardContent className="flex flex-col gap-2 p-4 text-sm">
                  <p className="font-medium">Lead forms require Growth</p>
                  <p className="text-muted-foreground">
                    Upgrade to Growth or above to create and manage lead forms.
                  </p>
                  <div>
                    <Button asChild size="sm">
                      <Link to="/upgrade" search={{ plan: 'growth_monthly' }}>
                        View upgrade options
                      </Link>
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
            <VirtualizedDataTable
              className="min-h-0 flex-1"
              columns={getFormColumns(allowPublicLinks, allowPublicIdentifier)}
              data={forms ?? []}
              isLoading={isFormsPending}
              onRowSelected={handleOnClickEdit}
              onRowDeleted={(form) => setFormToDelete(form)}
              stickyHeader
              tableContainerClassName="min-h-0 flex-1"
              tableWrapperClassName="h-full min-h-0"
              tableClassName="table-fixed min-w-[672px]"
              virtualizedHeight="100%"
              estimateRowHeight={64}
            />
          </div>
        </div>
      </div>
    </>
  )
}
